import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
	Box,
	Typography,
	Autocomplete,
	TextField,
	Chip,
} from '@mui/material'
import { Sensors as SensorIcon } from '@mui/icons-material'
import { useAuth } from '../contexts/auth-context'
import { useDemoMode } from '../contexts/demo-mode-context'
import { supabase } from '../lib/supabase'
import { useAllCustomersSensors } from '../hooks/use-all-customers-sensors'
import { useRealtime } from '../hooks/use-realtime'
import RealtimeTab from '../components/RealtimeTab'
import { PageContainer } from '../components/PageContainer'
import { buildCfgSearchParams, buildAllScopeCfgSearchParams, resolveSensorIdFromUrl } from '../utils/sensor-url-params'
import { demoWaterLabelFromMap, useDemoWaterLabelBySensorId } from '../hooks/use-demo-water-labels'

export function DashboardPage() {
	const { customerId, isAllScope } = useAuth()
	const { redactionActive } = useDemoMode()
	const [searchParams, setSearchParams] = useSearchParams()

	// ── Single-customer state ────────────────────────────────────────────────
	const [sensors, setSensors] = useState([])
	const [sensorId, setSensorId] = useState(null)
	const [loading, setLoading] = useState(true)
	const demoWaterIndexBySensorId = useDemoWaterLabelBySensorId(sensors)
	const demoWaterLabel = (id) => demoWaterLabelFromMap(demoWaterIndexBySensorId, id)

	// ── All-scope state ──────────────────────────────────────────────────────
	const { allSensorsList, loading: allLoading } = useAllCustomersSensors({
		enabled: isAllScope,
		includeLatest: false,
	})
	const [allScopeSensorId, setAllScopeSensorId] = useState(null)
	const [allScopeCustomerId, setAllScopeCustomerId] = useState(null)

	// Resolve which customerId / sensorId to feed into useRealtime
	const realtimeCustomerId = isAllScope ? allScopeCustomerId : customerId
	const realtimeSensorId = isAllScope ? allScopeSensorId : sensorId

	const { history, availableFields, status } = useRealtime(realtimeCustomerId, realtimeSensorId)

	// ── Single-customer: fetch sensors ───────────────────────────────────────
	useEffect(() => {
		if (isAllScope || !customerId) return

		const fetchSensors = async () => {
			const { data } = await supabase
				.from('sensor_configs')
				.select('id, sensor_id, sensor_location')
				.eq('customer_id', customerId)
				.eq('delivery_enabled', true)
				.eq('realtime_enabled', true)
				.order('sensor_location')

			setSensors(data && data.length > 0 ? data : [])
			setLoading(false)
		}

		fetchSensors()
	}, [customerId, isAllScope])

	// ── Single-customer: sync URL ↔ selected sensor ──────────────────────────
	useEffect(() => {
		if (isAllScope || loading) return
		if (sensors.length === 0) {
			setSensorId(null)
			return
		}

		const { sensorId: resolved, configId } = resolveSensorIdFromUrl(searchParams, sensors)

		if (resolved) {
			setSensorId(resolved)
			if (configId != null) {
				const cfgStr = String(configId)
				if (searchParams.get('cfg') !== cfgStr || searchParams.has('sensor')) {
					setSearchParams(buildCfgSearchParams(configId), { replace: true })
				}
			}
			return
		}

		const first = sensors[0]
		setSensorId(first.sensor_id)
		const cfgStr = String(first.id)
		if (searchParams.get('cfg') !== cfgStr || searchParams.has('sensor')) {
			setSearchParams(buildCfgSearchParams(first.id), { replace: true })
		}
	}, [loading, isAllScope, sensors, searchParams, setSearchParams])

	// ── All-scope: sync URL ↔ selected sensor ────────────────────────────────
	useEffect(() => {
		if (!isAllScope || allLoading) return
		if (allSensorsList.length === 0) {
			setAllScopeSensorId(null)
			setAllScopeCustomerId(null)
			return
		}

		const { sensorId: resolved, configId } = resolveSensorIdFromUrl(searchParams, allSensorsList)

		if (resolved) {
			const row = allSensorsList.find((s) => s.sensor_id === resolved)
			setAllScopeSensorId(resolved)
			setAllScopeCustomerId(row?.customer_id || null)
			if (configId != null && row) {
				const custStr = String(row.customer_id)
				const cfgStr = String(configId)
				if (searchParams.get('cfg') !== cfgStr || searchParams.get('cust') !== custStr) {
					setSearchParams(buildAllScopeCfgSearchParams(configId, row.customer_id), { replace: true })
				}
			}
			return
		}

		const first = allSensorsList[0]
		setAllScopeSensorId(first.sensor_id)
		setAllScopeCustomerId(first.customer_id)
		setSearchParams(buildAllScopeCfgSearchParams(first.id, first.customer_id), { replace: true })
	}, [isAllScope, allLoading, allSensorsList, searchParams, setSearchParams])

	const handleSensorChange = (newSensorId) => {
		if (isAllScope) {
			const row = allSensorsList.find((s) => s.sensor_id === newSensorId)
			setAllScopeSensorId(newSensorId)
			setAllScopeCustomerId(row?.customer_id || null)
			if (row) {
				setSearchParams(buildAllScopeCfgSearchParams(row.id, row.customer_id), { replace: true })
			}
		} else {
			const row = sensors.find((s) => s.sensor_id === newSensorId)
			setSensorId(newSensorId)
			if (row) {
				setSearchParams(buildCfgSearchParams(row.id), { replace: true })
			}
		}
	}

	const activeSensors = isAllScope ? allSensorsList : sensors
	const activeSensorId = isAllScope ? allScopeSensorId : sensorId
	const isLoading = isAllScope ? allLoading : loading

	if (isLoading) return <></>

	if (activeSensors.length === 0) {
		return (
			<PageContainer>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
					<Typography color="text.secondary">No sensors configured for this account.</Typography>
				</Box>
			</PageContainer>
		)
	}

	return (
		<PageContainer>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h4" fontWeight="bold" gutterBottom>
					Real-Time Dashboard
				</Typography>
				<Typography variant="body1" color="text.secondary" mb={3}>
					Live sensor data and performance metrics
				</Typography>

				<Autocomplete
					value={activeSensors.find((s) => s.sensor_id === activeSensorId) || null}
					onChange={(_, newValue) => {
						if (newValue) handleSensorChange(newValue.sensor_id)
					}}
					options={activeSensors}
					groupBy={isAllScope ? (option) => option.customerName : undefined}
					getOptionLabel={(option) => {
						if (!option) return ''
						if (redactionActive) {
							return `${option.sensor_id} — ${demoWaterLabel(option.sensor_id)}`
						}
						const label = option.sensor_location || option.sensor_id
						return isAllScope ? `${option.customerName} — ${label}` : label
					}}
					isOptionEqualToValue={(option, value) => option.sensor_id === value.sensor_id}
					sx={{ maxWidth: 500 }}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Select Sensor"
							placeholder="Choose a sensor..."
						/>
					)}
					renderOption={(props, option) => (
						<Box
							component="li"
							{...props}
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'flex-start !important',
								py: 1.5,
								px: 2,
								'&:hover': { backgroundColor: 'action.hover' },
							}}
						>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
								<Chip
									label={
										redactionActive
											? demoWaterLabel(option.sensor_id)
											: option.sensor_location || 'No location'
									}
									size="small"
									color="primary"
									sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}
								/>
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
								<SensorIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
								<Typography variant="body2" color="text.secondary">
									{option.sensor_id}
								</Typography>
							</Box>
						</Box>
					)}
				/>
			</Box>

			{realtimeSensorId && (
				<RealtimeTab
					history={history}
					availableFields={availableFields}
					status={status}
				/>
			)}
		</PageContainer>
	)
}
